import type { QueueDiagnosticEvent, QueueJobArgs, QueueOptions, QueueProcessResult } from "./types";
import { QueueDiagnosticEventType } from "./types";

export class UploadQueueManager<TJob, TResult> {
  private readonly queuedJobs: TJob[] = [];
  private readonly queuedJobKeys = new Set<string>();
  private isRunning = false;

  /**
   * Bumped by reset().
   *
   * If an older queue run is still waiting on a job when that happens, this is
   * how it notices it has gone stale and stops after the in-flight job finishes.
   */
  private queueGeneration = 0;

  constructor(private readonly options: QueueOptions<TJob, TResult>) {}

  enqueue({ job }: QueueJobArgs<TJob>): void {
    const jobKey = this.options.dedupeKey({ job });

    if (this.queuedJobKeys.has(jobKey)) {
      const existing = this.queuedJobs.find(
        (queued) => this.options.dedupeKey({ job: queued }) === jobKey
      );

      if (existing && this.options.mergeOnDuplicate) {
        this.options.mergeOnDuplicate({
          existingJob: existing,
          incomingJob: job,
        });
        this.emitDiagnosticEvent({
          type: QueueDiagnosticEventType.DuplicateJobMerged,
          existingJob: existing,
          incomingJob: job,
          jobKey,
          queueSize: this.queuedJobs.length,
        });
      } else {
        this.emitDiagnosticEvent({
          type: QueueDiagnosticEventType.DuplicateJobDropped,
          incomingJob: job,
          jobKey,
          queueSize: this.queuedJobs.length,
        });
      }

      return;
    }

    this.queuedJobKeys.add(jobKey);
    this.queuedJobs.push(job);
    this.emitDiagnosticEvent({
      type: QueueDiagnosticEventType.JobEnqueued,
      job,
      jobKey,
      queueSize: this.queuedJobs.length,
    });
    void this.runQueue();
  }

  reset(): void {
    const clearedJobCount = this.queuedJobs.length;
    this.queueGeneration += 1;
    this.queuedJobs.splice(0);
    this.queuedJobKeys.clear();
    this.emitDiagnosticEvent({
      type: QueueDiagnosticEventType.QueueReset,
      clearedJobCount,
      nextRunGeneration: this.queueGeneration,
    });
  }

  isActive(): boolean {
    return this.isRunning;
  }

  private readonly toErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  };

  private readonly reportProcessResult = ({
    job,
    jobKey,
    queueSize,
    runGeneration,
    result,
  }: {
    job: TJob;
    jobKey: string;
    queueSize: number;
    runGeneration: number;
    result: QueueProcessResult<TResult>;
  }): void => {
    if (result.success) {
      this.emitDiagnosticEvent({
        type: QueueDiagnosticEventType.JobSucceeded,
        job,
        jobKey,
        queueSize,
        runGeneration,
      });
      this.options.callbacks?.onSuccess?.({
        job,
        result: result.result,
      });

      return;
    }

    this.emitDiagnosticEvent({
      type: QueueDiagnosticEventType.JobFailed,
      job,
      jobKey,
      queueSize,
      runGeneration,
      errorMessage: result.error,
    });
    this.options.callbacks?.onError?.({
      job,
      error: result.error,
    });
  };

  private readonly reportProcessError = ({
    job,
    jobKey,
    queueSize,
    runGeneration,
    error,
  }: {
    job: TJob;
    jobKey: string;
    queueSize: number;
    runGeneration: number;
    error: unknown;
  }): void => {
    const errorMessage = this.toErrorMessage(error);
    this.emitDiagnosticEvent({
      type: QueueDiagnosticEventType.JobFailed,
      job,
      jobKey,
      queueSize,
      runGeneration,
      errorMessage,
    });
    this.options.callbacks?.onError?.({
      job,
      error: errorMessage,
    });
  };

  private readonly removeProcessedJob = ({ job }: { job: TJob }): void => {
    const jobKey = this.options.dedupeKey({ job });
    this.queuedJobs.shift();
    this.queuedJobKeys.delete(jobKey);
  };

  private readonly finishQueueRun = (): void => {
    this.isRunning = false;

    if (this.queuedJobs.length) {
      void this.runQueue();
    }
  };

  /**
   * Drains the queue one job at a time until there is nothing left to do.
   *
   * Rough flow:
   * - grab the next queued job
   * - run it and report the outcome
   * - stop here if reset() made this run stale while the job was in flight
   * - otherwise remove the processed job and keep going
   *
   * Note that if reset() is called while a job is already in flight, that job
   * is allowed to finish, but the older run does not keep draining the queue
   * after reset() has cleared and restarted things.
   */
  private async runQueue(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    const runGeneration = this.queueGeneration;

    try {
      while (true) {
        const nextJob = this.queuedJobs[0] ?? null;

        if (!nextJob) {
          return;
        }

        const jobKey = this.options.dedupeKey({ job: nextJob });
        this.emitDiagnosticEvent({
          type: QueueDiagnosticEventType.JobStarted,
          job: nextJob,
          jobKey,
          queueSize: this.queuedJobs.length,
          runGeneration,
        });

        try {
          const result: QueueProcessResult<TResult> = await this.options.process({
            job: nextJob,
          });

          this.reportProcessResult({
            job: nextJob,
            jobKey,
            queueSize: this.queuedJobs.length,
            runGeneration,
            result,
          });
        } catch (error) {
          this.reportProcessError({
            job: nextJob,
            jobKey,
            queueSize: this.queuedJobs.length,
            runGeneration,
            error,
          });
        }

        const isStaleRunGeneration = runGeneration !== this.queueGeneration;

        if (isStaleRunGeneration) {
          this.emitDiagnosticEvent({
            type: QueueDiagnosticEventType.StaleRunStopped,
            runGeneration,
            currentGeneration: this.queueGeneration,
          });
          return;
        }

        this.removeProcessedJob({ job: nextJob });
      }
    } finally {
      this.finishQueueRun();
    }
  }

  private readonly emitDiagnosticEvent = (event: QueueDiagnosticEvent<TJob>): void => {
    try {
      this.options.diagnostics?.onEvent?.({ event });
    } catch {
      // Diagnostics are best-effort and must not change queue behavior.
    }
  };
}
